// React
import React, { useEffect, useState, cloneElement, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'

// Libraries
import { FocusedImage } from 'image-focus'
import _isEmpty from 'lodash/isEmpty'

// Components
import BFocalImage from 'ui/blocks/FocalImage'

const FocalImage = (props) => {
  const [loading, setLoading] = useState(true)

  const ref = useRef(null)
  const fit = props.fit || 'cover'
  const shouldFocus = fit !== 'contain'

  // Use primitive values for coordinates to avoid infinite re-renders from object reference changes
  const coordX = props.coordinates?.x ?? 0
  const coordY = props.coordinates?.y ?? 0

  const applyFocus = useCallback(() => {
    if (ref.current && ref.current.src && shouldFocus) {
      const coordinates = _isEmpty(props.coordinates) ? { x: 0, y: 0 } : props.coordinates

      new FocusedImage(ref.current, {
        focus: coordinates
      })
    }
    setLoading(false)
  }, [coordX, coordY, shouldFocus])

  useEffect(() => {
    if (shouldFocus) {
      setLoading(true)
      applyFocus()
    } else if (ref.current?.complete) {
      setLoading(false)
    }
  }, [coordX, coordY, shouldFocus, applyFocus])

  const onImageLoad = () => {
    applyFocus()
  }

  return (
    <BFocalImage
      size={props.size}
      width={props.width}
      height={props.height}
      selected={props.selected}
      noBorder={props.noBorder}
      noBorderRadius={props.noBorderRadius}
      maintainAspect={props.maintainAspect}
      onClick={props.onClick}
      data-tid={props.target}
      data-tip={props.tip}
      cursor={props.cursor}
      inline={props.inline}
      opacity={props.opacity}
    >
      {props.children && cloneElement(props.children)}
      {props.preview && loading && <BFocalImage.Preview src={`data:image/jpeg;base64,${props.preview}`} />}
      <BFocalImage.Image isLoading={loading} src={props.url} ref={ref} onLoad={onImageLoad} alt={props.alt} fit={fit} />
    </BFocalImage>
  )
}

FocalImage.displayName = 'FocalImage'

FocalImage.propTypes = {
  coordinates: PropTypes.object,
  url: PropTypes.string,
  selected: PropTypes.bool,
  children: PropTypes.node,
  size: PropTypes.string,
  width: PropTypes.string,
  height: PropTypes.string,
  noBorder: PropTypes.bool,
  noBorderRadius: PropTypes.bool,
  preview: PropTypes.string,
  maintainAspect: PropTypes.bool,
  onClick: PropTypes.func,
  target: PropTypes.string,
  tip: PropTypes.string,
  cursor: PropTypes.string,
  alt: PropTypes.string,
  inline: PropTypes.bool,
  opacity: PropTypes.number,
  fit: PropTypes.oneOf(['cover', 'contain'])
}

export default FocalImage
